/**
 * Dev-only API audit chip — shown in the ticker workbench header for admins.
 * Reads from /api/debug/api-audit/ticker/{symbol} (in-memory counters).
 * Not a permanent feature; safe to delete post-audit.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const VENDOR_COLORS = {
  tradier: "#22C55E",
  yfinance: "#F59E0B",
  intrinio: "#3B82F6",
  alpaca: "#A855F7",
  financialdata_net: "#06B6D4",
  mongo: "#A1A1AA",
  memory_cache: "#94A3B8",
};

export default function ApiAuditChip({ symbol }) {
  const { user } = useAuth();
  const [snap, setSnap] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin || !symbol) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/debug/api-audit/ticker/${symbol}`);
        if (!cancelled) setSnap(data);
      } catch (e) { /* ignore */ }
    };
    load();
    timerRef.current = setInterval(load, 2000);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [symbol, isAdmin]);

  if (!isAdmin) return null;

  const total = snap?.total_calls || 0;
  const byVendor = snap?.by_vendor || [];
  const hits = snap?.cache_hits || 0;
  const misses = snap?.cache_misses || 0;
  const last = snap?.last_action;

  const resetTicker = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await api.post(`/debug/api-audit/ticker/${symbol}/reset`);
      setSnap({ ticker: symbol, total_calls: 0, by_vendor: [], by_module: [], cache_hits: 0, cache_misses: 0 });
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="api-audit-chip"
        title="Dev-only · click to expand vendor call breakdown"
        className="text-[10px] font-bold px-1.5 py-0.5 border cursor-pointer hover:opacity-90"
        style={{
          background: "rgba(59,130,246,0.12)",
          color: "#3B82F6",
          borderColor: "rgba(59,130,246,0.35)",
        }}
      >
        API {total}
        {byVendor.map((v) => (
          <span key={v.vendor} className="ml-1" style={{ color: VENDOR_COLORS[v.vendor] || "#3B82F6" }}>
            · {v.vendor.slice(0, 4)} {v.count}
          </span>
        ))}
        <span className="ml-1 opacity-70">· hits {hits}/{hits + misses}</span>
        {last?.module && <span className="ml-1 opacity-70">· {last.module}</span>}
      </button>

      {open && (
        <div
          data-testid="api-audit-panel"
          className="absolute z-50 mt-6 right-4 p-3 border rounded-sm min-w-[420px] max-w-[560px] shadow-lg"
          style={{
            background: "var(--hoip-card)",
            borderColor: "var(--hoip-border)",
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-bold">API AUDIT · {symbol}</div>
            <div className="flex items-center gap-2">
              <button onClick={resetTicker} disabled={busy}
                className="text-[10px] px-2 py-0.5 border hover:opacity-80 disabled:opacity-40"
                style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>
                RESET
              </button>
              <button onClick={() => setOpen(false)} className="text-[10px] opacity-70 hover:opacity-100">✕</button>
            </div>
          </div>
          <div className="text-[10px] opacity-70 mb-1">
            External vendor calls vs Mongo / in-memory cache. URLs, keys, and headers are never shown.
            {snap?.used_cached_snapshot ? " This screen used a cached snapshot." : " This window recorded at least one external call."}
            {((snap?.by_vendor || []).some((v) => v.vendor === "yfinance") && !(snap?.by_vendor || []).some((v) => v.vendor === "financialdata_net"))
              ? " yfinance rows are fallback or historical events — house feed calls appear as financialdata_net when that provider is selected."
              : ""}
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3 text-[11px]">
            <div className="border p-2" style={{ borderColor: "var(--hoip-border)" }}>
              <div className="opacity-60 text-[9px] tracking-wide">EXTERNAL</div>
              <div className="font-mono-data text-lg">{snap?.total_external_calls ?? total}</div>
            </div>
            <div className="border p-2" style={{ borderColor: "var(--hoip-border)" }}>
              <div className="opacity-60 text-[9px] tracking-wide">CACHE HIT RATE</div>
              <div className="font-mono-data text-lg">
                {hits + misses > 0 ? Math.round((hits / (hits + misses)) * 100) : 0}%
              </div>
              <div className="opacity-60 text-[9px]">{hits} hit / {misses} miss</div>
            </div>
            <div className="border p-2" style={{ borderColor: "var(--hoip-border)" }}>
              <div className="opacity-60 text-[9px] tracking-wide">LAST ACTION</div>
              <div className="font-mono-data text-[10px]">{last?.module || "—"}</div>
              <div className="opacity-60 text-[9px]">{last?.vendor}</div>
            </div>
          </div>

          <div className="text-[10px] font-bold opacity-70 mb-1">BY VENDOR</div>
          <table className="w-full text-[11px] mb-3">
            <tbody>
              {byVendor.length === 0 && (
                <tr><td className="opacity-50">No vendor calls recorded for this ticker.</td></tr>
              )}
              {byVendor.map((v) => (
                <tr key={v.vendor}>
                  <td style={{ color: VENDOR_COLORS[v.vendor] || "inherit" }}>{v.vendor}</td>
                  <td className="text-right font-mono-data">{v.count}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="text-[10px] font-bold opacity-70 mb-1">MONGO / CACHE</div>
          <div className="text-[11px] font-mono-data mb-3" data-testid="api-audit-mongo">
            Mongo reads {snap?.mongo?.reads ?? 0} · hits {snap?.mongo?.hits ?? 0} · misses {snap?.mongo?.misses ?? 0}
            {snap?.mongo?.latency_ms_total != null ? ` · ${snap.mongo.latency_ms_total} ms` : ""}
            <div className="opacity-70 text-[10px] mt-0.5">
              Last external refresh: {snap?.last_external_refresh ? new Date(snap.last_external_refresh).toLocaleTimeString() : "none in this window"}
            </div>
          </div>

          <div className="text-[10px] font-bold opacity-70 mb-1">BY MODULE</div>
          <table className="w-full text-[11px] mb-2">
            <thead>
              <tr className="opacity-60 text-[9px]">
                <th className="text-left">Module</th>
                <th className="text-right">Calls</th>
                <th className="text-left pl-2">Vendors</th>
                <th className="text-right">Cache</th>
              </tr>
            </thead>
            <tbody>
              {(snap?.by_module || []).map((m) => (
                <tr key={m.module}>
                  <td className="font-mono-data">{m.module}</td>
                  <td className="text-right">{m.calls}</td>
                  <td className="pl-2 opacity-80 text-[10px]">
                    {Object.entries(m.vendors || {}).map(([v, n]) => `${v}×${n}`).join(", ")}
                  </td>
                  <td className="text-right text-[10px] opacity-70">
                    {m.cache_hits}/{m.cache_hits + m.cache_misses}
                  </td>
                </tr>
              ))}
              {(!snap?.by_module || snap.by_module.length === 0) && (
                <tr><td colSpan={4} className="opacity-50">No module data.</td></tr>
              )}
            </tbody>
          </table>

          <div className="text-[10px] font-bold opacity-70 mb-1">RECENT (last 20)</div>
          <div className="max-h-40 overflow-y-auto text-[10px] font-mono-data">
            {(snap?.recent_20 || []).slice().reverse().map((e, i) => (
              <div key={i} className="flex justify-between gap-2 py-0.5 border-b" style={{ borderColor: "var(--hoip-divider)" }}>
                <span className="opacity-60">{new Date(e.ts).toLocaleTimeString()}</span>
                <span style={{ color: VENDOR_COLORS[e.vendor] || "inherit" }}>{e.vendor}</span>
                <span className="flex-1 truncate opacity-80">{e.module || "—"}</span>
                <span className={e.cache === "hit" ? "text-emerald-500" : "opacity-60"}>{e.cache}</span>
              </div>
            ))}
            {(!snap?.recent_20 || snap.recent_20.length === 0) && (
              <div className="opacity-50">No recent events.</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
